import { LicenseDto } from '@thingslog/repositories';
import React, { useEffect, useMemo } from 'react';
import { licenseQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import Header from '../../components/header';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { LicenseWarningBox } from '@thingslog/ui-components';
import JwtValidator from '../../common/JwtValidator';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';

const LicenseError: React.FC<LicenseErrorProps> = (props: LicenseErrorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { useLicenses } = useMemo(() => licenseQueryClient, []);
  const jwtValidator = useMemo(() => new JwtValidator(), []);

  const companyId = useDetermineCompanyId();

  const { data: licenses, isLoading } = useLicenses(Number(companyId), undefined, undefined, {
    refetchOnWindowFocus: false,
    enabled: companyId !== null,
  });

  const foundInactiveLicense = licenses?.some((license: LicenseDto) => !license.isActive) ?? false;
  const invalidLicenses = !licenses?.some((license: LicenseDto) => license.isValid);

  useEffect(() => {
    // Keycloak returns the user to the page they were signed out on, so leave this page
    // as soon as the current user turns out to have access.
    if (jwtValidator.hasRole('ROLE_SUPER_ADMIN')) {
      navigate('/app', { replace: true });
      return;
    }

    if (!licenses) return;

    const hasAccess =
      licenses.length === 0 ||
      licenses.some((license: LicenseDto) => license.isActive && license.isValid);

    if (hasAccess) {
      navigate('/app', { replace: true });
    }
  }, [licenses, jwtValidator, navigate]);

  return (
    <Header>
      {isLoading ? (
        <CircularProgress size={80} />
      ) : (
        <div>
          <h1 className="text-gray-900 font-bold">{t('license_error_page_access_restricted')}</h1>
          <div className="text-justify flex flex-col justify-center items-center ">
            <LicenseWarningBox
              warningStatus={{
                noActiveLicense: foundInactiveLicense,
                noValidLicense: invalidLicenses,
                devicesCountLimitReached: false,
              }}
              translation={{
                accessOnHoldTitle: t('license_warning_access_hold'),
                accessOnHoldDescription: t('license_warning_access_hold_description'),
                noValidLicenseTitle: t('license_warning_no_valid_license'),
                noValidLicenseDescription: t('license_warning_no_valid_license_description'),
                deviceLimitReachedTitle: t('license_warning_devices_count_limit_reached'),
                deviceLimitReachedDescription: t(
                  'license_warning_devices_count_limit_reached_description'
                ),
                defaultGenericTitle: t('license_warning_generic_title'),
                defaultGenericDescription: t('license_warning_generic_description'),
              }}
            />
          </div>
        </div>
      )}
    </Header>
  );
};

interface LicenseErrorProps {}

export default LicenseError;
